import React, { useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { DataRoomShareableLink } from 'models/ShareableLink';
import { Button, Loader } from 'ncoded-component-library';
import ResolvePagination from 'components/ResolvePagination';
import { useTranslation } from 'react-i18next';
import ShareableLinksMobileItem from './components/ShareableLinksMobileItem';
import CreateShareableLinkContext from 'router/subrouters/DataRoom/providers/CreateShareableLink/CreateShareableLink.context';
import MultiSort from 'components/MultiSort';
import {
  SORTING_VALUES,
  SL_SORT_DIRECTION_COPY,
} from '../ShareableLinksTable/components/data';
import { OptionValue } from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import { SORTING_ORDER, SortingDirectionValues } from 'models/Sort';

import './ShareableLinksMobile.styles.scss';

const HEADING_TRANSLATIONS: Record<string, string> = {
  name: 'name',
  accessNumber: 'access',
  totalAccessCount: 'visitsUC',
  fileIds: 'files',
  lastAccessDate: 'lastAccess',
  createdAt: 'created',
  linkExpiresDate: 'expiresOn',
};

type ShareableLinksMobileProps = {
  className?: string;
  isLoading: boolean;
  totalPages: number;
  shareableLinks: DataRoomShareableLink[];
  updateShareableLink: (link: DataRoomShareableLink) => void;
  onAfterToggleState?: (linkBeforeToggle: DataRoomShareableLink) => void;
};

const ShareableLinksMobile: React.FC<ShareableLinksMobileProps> = (props) => {
  const {
    className,
    isLoading,
    totalPages,
    shareableLinks,
    updateShareableLink,
    onAfterToggleState,
  } = props;

  const [expandedLinkId, setExpandedLinkId] = useState(null);

  const { openCreateShareableLink } = useContext(CreateShareableLinkContext);

  const loading = useMemo(
    () => isLoading && !shareableLinks?.length,
    [isLoading, shareableLinks?.length],
  );

  const classes = classNames('shareable-links-mobile', { loading }, className);

  const { t } = useTranslation();

  const multiSortValues = useMemo(
    () =>
      Object.entries(SORTING_VALUES).map(([key, value]) => ({
        value,
        label: t(HEADING_TRANSLATIONS[key]),
      })) as OptionValue<string>[],
    [t],
  );

  const multiSortDirectionTranslation = useMemo(
    () =>
      Object.entries(SL_SORT_DIRECTION_COPY).reduce<SortingDirectionValues>(
        (acc, [field, directions]) => {
          acc[field] = {
            [SORTING_ORDER.A_TO_Z]: t(directions[SORTING_ORDER.A_TO_Z]),
            [SORTING_ORDER.Z_TO_A]: t(directions[SORTING_ORDER.Z_TO_A]),
          };
          return acc;
        },
        {},
      ),
    [t],
  );
  return (
    <div className={classes}>
      {loading && <Loader />}

      <div className="shareable-links-mobile__header">
        <Button variant="outline" onClick={() => openCreateShareableLink()}>
          {t('createSecureLink')}
        </Button>
        <MultiSort
          multiSortValues={multiSortValues}
          multiSortDirectionTranslation={multiSortDirectionTranslation}
        />
      </div>

      <div className="white-panel">
        {shareableLinks.map((link) => (
          <ShareableLinksMobileItem
            key={link?._id}
            link={link}
            isExpanded={expandedLinkId === link?._id}
            updateShareableLink={updateShareableLink}
            onAfterToggleState={onAfterToggleState}
            onRowClick={({ _id }) =>
              setExpandedLinkId(expandedLinkId === _id ? null : _id)
            }
          />
        ))}
      </div>

      <ResolvePagination totalPages={totalPages} />
    </div>
  );
};

export default ShareableLinksMobile;
